// ============================================================
// News Section — displayed at bottom of stock report page
// Shows latest 5 high-impact news items for the stock.
//
// Priority: reads from stored report content (newsItems field).
// Fallback: fetches from /api/news/stock for older reports that
// were generated before news was stored in the content JSON.
//
// If no news available from either source, renders nothing.
// This section is NOT paywalled — visible to free and pro users.
// ============================================================

"use client";

import { useEffect, useState } from "react";
import { Newspaper } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import type { StockNewsItem } from "@/lib/data/news-client";

interface NewsSectionProps {
  ticker: string;
  companyName: string;
  exchange: string;
  /** Pre-loaded news from report content JSON (new reports have this) */
  storedNews?: StockNewsItem[];
}

/** Color mapping for the impact indicator dot */
const impactDot: Record<string, string> = {
  positive: "bg-cgreen",
  negative: "bg-cred",
  neutral: "bg-dim",
};

/**
 * Renders a news section at the bottom of the report page.
 * Uses stored news from the report content if available.
 * Falls back to /api/news/stock for older reports.
 * Renders nothing when no news is available.
 */
export function NewsSection({ ticker, companyName, exchange, storedNews }: NewsSectionProps) {
  const [news, setNews] = useState<StockNewsItem[] | null>(
    storedNews && storedNews.length > 0 ? storedNews : null
  );
  const [loading, setLoading] = useState(!storedNews || storedNews.length === 0);

  useEffect(() => {
    // If we already have stored news, skip the API call
    if (storedNews && storedNews.length > 0) return;

    // Fallback: fetch from API for older reports without stored news
    (async () => {
      try {
        const params = new URLSearchParams({ ticker, name: companyName, exchange });
        const res = await fetch(`/api/news/stock?${params}`);
        const json = await res.json();
        if (json.success && json.data?.news?.length > 0) {
          setNews(json.data.news);
        }
      } catch {
        // News is non-critical — silently fail
      } finally {
        setLoading(false);
      }
    })();
  }, [ticker, companyName, exchange, storedNews]);

  // Loading skeleton — 3 pulsing lines matching existing skeleton pattern
  if (loading) {
    return (
      <div className="bg-surface border border-surface-light/30 rounded-xl p-5 mt-5">
        <div className="flex items-center gap-2 mb-4">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-52" />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="mb-3 last:mb-0">
            <Skeleton className="h-4 w-full mb-1.5" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  // No news available — render nothing
  if (!news || news.length === 0) return null;

  return (
    <div className="bg-surface border border-surface-light/30 rounded-xl p-5 mt-5" id="news-section">
      <div className="flex items-center gap-2 mb-4">
        <Newspaper className="w-4 h-4 text-blue" />
        <h3 className="text-foreground text-[15px] font-serif m-0">
          Recent News & Developments
        </h3>
      </div>

      <div className="space-y-3">
        {news.map((item, i) => (
          <div
            key={i}
            className="flex gap-3 py-2.5 border-b border-surface-light/20 last:border-0 last:pb-0"
          >
            {/* Impact indicator dot */}
            <div className="pt-1.5 shrink-0">
              <div className={`w-2 h-2 rounded-full ${impactDot[item.impact] ?? impactDot.neutral}`} />
            </div>

            <div className="min-w-0 flex-1">
              {/* Headline — links to source article if URL available */}
              {item.url ? (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground text-sm font-semibold leading-snug mb-1 block hover:text-blue transition no-underline"
                >
                  {item.headline}
                </a>
              ) : (
                <div className="text-foreground text-sm font-semibold leading-snug mb-1">
                  {item.headline}
                </div>
              )}

              {/* Summary */}
              <div className="text-muted-foreground text-[12px] leading-relaxed mb-1">
                {item.summary}
              </div>

              {/* Source + date */}
              <div className="text-dim text-[11px]">
                {item.source} &bull;{" "}
                {new Date(item.date + "T00:00:00").toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
